import React, { useState, useRef, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Input } from './input';
import { toast } from 'react-hot-toast';
import { useUpmTags } from '../../hooks/useUpmMasters';
import api from '../../lib/api';
import { useQueryClient } from '@tanstack/react-query';

interface Tag {
  id: number;
  name: string;
}

interface TagsInputProps {
  value: Tag[];
  onChange: (tags: Tag[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: string;
}

const TagsInput: React.FC<TagsInputProps> = ({
  value = [],
  onChange,
  placeholder = "Type to search or add tags...",
  className,
  disabled = false,
  error,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const queryClient = useQueryClient();

  // Use the real API hook
  const { data: suggestions = [], isLoading: loading, error: searchError, refetch: refetchTags } = useUpmTags(inputValue, inputValue.length >= 3);


  // Add new tag to master
  const addTagToMaster = async (tagName: string): Promise<Tag | null> => {
    try {
      const response = await api.createUpmMasters('tags', tagName);
      if (response.success) {
        toast.success('Tag added successfully');
        // Invalidate all tag queries to refresh cache
        await queryClient.invalidateQueries({ queryKey: ['upm-tags'] });
        // Refetch tags to get the new one
        await refetchTags();
        return response.data[0];
      } else {
        toast.error(response.message || 'Failed to add tag');
        return null;
      }
    } catch (error) {
      console.error('Error adding tag:', error);
      toast.error('Failed to add tag');
      return null;
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setInputValue(query);

    if (query.trim()) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  // Handle tag selection
  const handleTagSelect = (tag: Tag) => {
    if (!value.find(t => t.id === tag.id)) {
      onChange([...value, tag]);
    }
    setInputValue('');
    setIsOpen(false);
  };

  // Handle adding new tag
  const handleAddNewTag = async () => {
    const tagName = inputValue.trim();
    if (!tagName) return;

    // Check if tag already exists
    const existingTag = value.find(t => t.name.toLowerCase() === tagName.toLowerCase());
    if (existingTag) {
      toast.error('Tag already exists');
      return;
    }

    const newTag = await addTagToMaster(tagName);
    if (newTag) {
      // Auto-select the newly created tag
      onChange([...value, newTag]);
      setInputValue('');
      setIsOpen(false);
    }
  };

  // Handle tag removal
  const handleTagRemove = (tagId: number) => {
    onChange(value.filter(tag => tag.id !== tagId));
  };

  // Reset highlighted index when suggestions change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [suggestions, inputValue]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex]);

  // Handle key down
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        handleTagSelect(suggestions[highlightedIndex]);
      } else if (suggestions.length > 0) {
        handleTagSelect(suggestions[0]);
      } else if (inputValue.trim()) {
        handleAddNewTag();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen && inputValue.trim()) {
        setIsOpen(true);
      }
      const totalOptions = suggestions.length + (inputValue.trim() ? 1 : 0);
      setHighlightedIndex(prev => (prev < totalOptions - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (inputValue.trim()) {
      setIsOpen(true);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={cn("space-y-3 relative", className)}>
      <div className="grid grid-cols-2 gap-4">
        {/* Input field - 1 column */}
        <div className="space-y-2 relative">
          <label className="text-sm font-medium text-gray-700">Add Tags</label>
          <Input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            placeholder="Type to search..."
            disabled={disabled}
            className="w-full bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]"
          />

          {/* Dropdown with suggestions */}
          {isOpen && (
            <div
              ref={dropdownRef}
              className="absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
            >
              {loading ? (
                <div className="p-2 text-sm text-gray-500">Loading...</div>
              ) : suggestions.length > 0 || inputValue.trim() ? (
                <>
                  {suggestions.map((tag: Tag, index: number) => {
                    const isHighlighted = highlightedIndex === index;
                    return (
                      <button
                        key={tag.id}
                        ref={(el) => { optionRefs.current[index] = el; }}
                        type="button"
                        onClick={() => handleTagSelect(tag)}
                        className={cn(
                          "w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-black",
                          isHighlighted && "bg-gray-100"
                        )}
                      >
                        {tag.name}
                      </button>
                    );
                  })}
                  {inputValue.trim() && (
                    (() => {
                      const addIndex = suggestions.length;
                      const isHighlighted = highlightedIndex === addIndex;
                      return (
                        <button
                          key="add-new"
                          ref={(el) => { optionRefs.current[addIndex] = el; }}
                          type="button"
                          onClick={handleAddNewTag}
                          className={cn(
                            "w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none flex items-center gap-2 text-black",
                            isHighlighted && "bg-gray-100"
                          )}
                        >
                          <Plus className="h-4 w-4" />
                          Add "{inputValue.trim()}"
                        </button>
                      );
                    })()
                  )}
                </>
              ) : null}
            </div>
          )}
        </div>

        {/* Tags display box - 1 column */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Selected Tags</label>
          <div className="min-h-[40px] border border-gray-300 rounded-md p-2 flex flex-wrap gap-1 bg-white">
            {value.length === 0 ? (
              <p className="text-sm text-gray-500 flex items-center h-full">
                No tags added yet
              </p>
            ) : (
              value.map((tag: Tag) => (
                <span
                  key={tag.id}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-md"
                >
                  {tag.name}
                  <button
                    type="button"
                    onClick={() => handleTagRemove(tag.id)}
                    className="hover:bg-blue-200 rounded-full p-0.5"
                    disabled={disabled}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default TagsInput;
